import type { Plan } from "./plans-client"

export type AiTokenFeature =
	| "copilot_draft"
	| "copilot_chat"
	| "docs_generation"
	| "docs_suggestion"
	| "docs_image"
	| "site_import"
	| "changelog"
	| "translation"
	| "auto_reply"
	| "thread_title"
	| "issue_summary"
	| "agent_chat"
	| "routines"
	| "close_loop"
	| "bug_inspect"
	| "agent_recommendations"
	| "request_triage"
	| "link_suggestions"
	| "slack_assistant"
	| "other"

export const AI_TOKEN_FEATURE_LABELS: Record<AiTokenFeature, string> = {
	copilot_draft: "AI Assistant drafts",
	copilot_chat: "AI Assistant chat",
	docs_generation: "Docs generation",
	docs_suggestion: "Docs suggestions",
	docs_image: "Image descriptions",
	site_import: "Help center import",
	changelog: "Changelog",
	translation: "Translation",
	auto_reply: "Auto-reply",
	thread_title: "Thread titles",
	issue_summary: "Issue summaries",
	agent_chat: "Support agent chat",
	routines: "Routines",
	close_loop: "Close loop follow-ups",
	bug_inspect: "Bug Inspect",
	agent_recommendations: "Agent recommendations",
	request_triage: "Feature request suggestions",
	link_suggestions: "Similar issue suggestions",
	slack_assistant: "Slack AI Assistant",
	other: "Other",
}

// agent_chat (the customer support agent) and auto_reply (the same agent
// answering by email, Slack and Teams) are billed per resolution, not per
// token. thread_title, site_import and docs_image run on their own, with
// nobody asking for them, so none of the three eats credits or the cap: an
// import of 150 screenshots used to spend a whole trial credit in one minute.
// Everything else, including the sidebar AI Assistant, is token-billed.
// agent_recommendations is the daily review of the agent's misses; we absorb
// it because its only output is a better agent. request_triage drafts the
// publish card on every portal request, which nobody asks for either.
// link_suggestions matches every new thread to open Linear issues, also unasked.
const NON_BILLABLE_FEATURES = new Set<AiTokenFeature>([
	"agent_chat",
	"auto_reply",
	"thread_title",
	"site_import",
	"docs_image",
	"agent_recommendations",
	"request_triage",
	"link_suggestions",
])

export function isBillableFeature(feature: AiTokenFeature): boolean {
	return !NON_BILLABLE_FEATURES.has(feature)
}

// Earns per resolution instead of per token, so its provider cost is reported
// apart from the features we give away for free.
const RESOLUTION_BILLED_FEATURES = new Set<AiTokenFeature>([
	"agent_chat",
	"auto_reply",
])

export function isResolutionBilledFeature(feature: AiTokenFeature): boolean {
	return RESOLUTION_BILLED_FEATURES.has(feature)
}

// Our margin on token usage: customers pay raw provider cost x this. 1.5 = a
// 50% fee on top of what the model actually costs us. Raise to charge more.
export const TOKEN_MARKUP = 1.5

interface ModelPrice {
	inputPer1M: number
	outputPer1M: number
	// A prompt-cache hit. 0.1x input on most models; gpt-4o-mini charges half
	// and claude-opus-5-5 charges 0.05x.
	cacheReadPer1M: number
	// Writing the prefix into the cache, at the 5 minute TTL we use. Anthropic
	// charges 2x input for the 1 hour TTL, so re-rate this if we ever set one.
	cacheWritePer1M: number
	// Anthropic bills the whole request at a higher rate once the prompt
	// (uncached, cache read and cache write tokens) passes the threshold.
	longPrompt?: { overTokens: number; price: Omit<ModelPrice, "longPrompt"> }
}

// USD cents per 1M tokens, matching provider list prices. Verified against
// platform.claude.com and developers.openai.com on Oct 7, 2026.
export const MODEL_PRICING: Record<string, ModelPrice> = {
	// $4 in / $20 out. Plans imported help centers and runs Bug Inspect.
	"claude-opus-5-5": {
		inputPer1M: 400,
		outputPer1M: 2000,
		cacheReadPer1M: 20,
		cacheWritePer1M: 500,
	},
	// $2 in / $10 out. Runs the agent, auto-replies and most generation.
	"claude-sonnet-5-5": {
		inputPer1M: 200,
		outputPer1M: 1000,
		cacheReadPer1M: 20,
		cacheWritePer1M: 250,
	},
	// $5 in / $25 out. Kept to price usage recorded before the 5.5 upgrade.
	"claude-opus-5": {
		inputPer1M: 500,
		outputPer1M: 2500,
		cacheReadPer1M: 50,
		cacheWritePer1M: 625,
	},
	// $2 in / $10 out. The launch price was introductory through Aug 31, 2026 and
	// the $3/$15 rise scheduled for Sept 1 was cancelled, so this is the standard
	// price now. Do not raise it.
	"claude-sonnet-5": {
		inputPer1M: 200,
		outputPer1M: 1000,
		cacheReadPer1M: 20,
		cacheWritePer1M: 250,
	},
	// $0.10 in / $0.50 out up to 100K prompt tokens, 5x past that. Runs the
	// docs gate, draft adjustments, summaries and classification.
	"claude-haiku-5-5": {
		inputPer1M: 10,
		outputPer1M: 50,
		cacheReadPer1M: 1,
		cacheWritePer1M: 12.5,
		longPrompt: {
			overTokens: 100_000,
			price: {
				inputPer1M: 50,
				outputPer1M: 250,
				cacheReadPer1M: 5,
				cacheWritePer1M: 62.5,
			},
		},
	},
	// $1 in / $5 out. Kept to price usage recorded before the 5.5 upgrade.
	"claude-haiku-4-5": {
		inputPer1M: 100,
		outputPer1M: 500,
		cacheReadPer1M: 10,
		cacheWritePer1M: 125,
	},
	// $4 in / $20 out. Drafts only. The Jul 30, 2026 cut reached Sol later than
	// Terra, so this was billed at the old $5/$30 until Sept 8, 2026.
	"gpt-5.6-sol": {
		inputPer1M: 400,
		outputPer1M: 2000,
		cacheReadPer1M: 40,
		cacheWritePer1M: 400,
	},
	// $2 in / $12 out after the Jul 30, 2026 cut.
	"gpt-5.6-terra": {
		inputPer1M: 200,
		outputPer1M: 1200,
		cacheReadPer1M: 20,
		cacheWritePer1M: 200,
	},
	// $10 in / $50 out. Writes changelogs.
	"gpt-6-astra": {
		inputPer1M: 1000,
		outputPer1M: 5000,
		cacheReadPer1M: 100,
		cacheWritePer1M: 1000,
	},
	// $1.25 in / $10 out. Kept for historical usage rows.
	"gpt-5": {
		inputPer1M: 125,
		outputPer1M: 1000,
		cacheReadPer1M: 12.5,
		cacheWritePer1M: 125,
	},
	// $0.15 in / $0.60 out, and its cache hits cost half input, not a tenth.
	"gpt-4o-mini": {
		inputPer1M: 15,
		outputPer1M: 60,
		cacheReadPer1M: 7.5,
		cacheWritePer1M: 15,
	},
}

// A model we have not priced yet. Keep it at or above the priciest model we
// run, or an unpriced row quietly undercharges.
const FALLBACK_PRICE: ModelPrice = {
	inputPer1M: 1000,
	outputPer1M: 5000,
	cacheReadPer1M: 100,
	cacheWritePer1M: 1250,
}

function priceForModel(model: string): ModelPrice {
	return MODEL_PRICING[model] ?? FALLBACK_PRICE
}

interface UsageInput {
	model: string
	inputTokens: number
	outputTokens: number
	// Anthropic reports both outside `input_tokens`, so they are separate here
	// too. A caller that leaves them out prices the uncached tokens only.
	cacheReadTokens?: number
	cacheWriteTokens?: number
}

// Raw provider cost in cents (fractional).
export function rawCostCents({
	model,
	inputTokens,
	outputTokens,
	cacheReadTokens = 0,
	cacheWriteTokens = 0,
}: UsageInput): number {
	const base = priceForModel(model)
	const promptTokens = inputTokens + cacheReadTokens + cacheWriteTokens
	const price =
		base.longPrompt && promptTokens > base.longPrompt.overTokens
			? base.longPrompt.price
			: base
	return (
		(inputTokens * price.inputPer1M +
			outputTokens * price.outputPer1M +
			cacheReadTokens * price.cacheReadPer1M +
			cacheWriteTokens * price.cacheWritePer1M) /
		1_000_000
	)
}

// Marked-up cost in cents (fractional).
export function billableCostCents(usage: UsageInput): number {
	return rawCostCents(usage) * TOKEN_MARKUP
}

// Marked-up cost in tenths of a cent, rounded to an integer. The running
// monthly spend counter accumulates in this unit so sub-cent calls are not
// rounded away to zero.
export function billableCostTenthCents(usage: UsageInput): number {
	return Math.round(billableCostCents(usage) * 10)
}

export function tenthCentsToCents(tenthCents: number): number {
	return Math.ceil(tenthCents / 10)
}

export function tenthCentsToUsd(tenthCents: number): number {
	return tenthCents / 1000
}

// Shown wherever a blocked AI feature has to explain itself to the user.
export const AI_BUDGET_REACHED_MESSAGE =
	"Your workspace has used its monthly AI budget, so AI generation is paused. Raise the AI spending limit in Settings to keep generating."

// A new workspace starts with room to spend past its credits. At 0 the agent
// stops the day the credits run out, which reads as a broken product.
export const AI_DEFAULT_OVERAGE_LIMIT_CENTS = 5_000

// The limit to offer someone whose AI is paused: the next 50 dollar step that
// clears what they already spent, so one click starts the agent again.
export function suggestedOverageLimitCents(overageUsedCents: number): number {
	const step = AI_DEFAULT_OVERAGE_LIMIT_CENTS
	return Math.max(step, Math.ceil((overageUsedCents + 1) / step) * step)
}

// Wire code for a request the AI spend cap refused. Clients switch on this;
// matching the message text drifts the moment someone rewords the copy.
export const AI_SPEND_LIMIT_ERROR_CODE = "ai_spend_limit_reached"

export const AI_SPEND_LIMIT_COPILOT_MESSAGE =
	"You've reached your monthly AI spend limit. Raise the limit in Settings to keep using the AI Assistant."

export const AI_SPEND_LIMIT_TRANSLATE_MESSAGE =
	"You've reached your monthly AI spend limit. Raise the limit or add a payment method to keep translating."

export function isAiSpendLimitCode(code: unknown): boolean {
	return code === AI_SPEND_LIMIT_ERROR_CODE
}

// Per-plan included AI credits before overage billing, in cents (shown with a
// $ sign, called "credits" in the UI). Included spend is pure cost, so keep it
// small; margin only comes from overage billed at TOKEN_MARKUP. The free tier's
// credits do NOT refresh monthly (see the reset cron).
export const FreeAiTokenSpendCents: Record<Plan, number> = {
	free: 200,
	starter: 500,
	pro: 1000,
	scale: 2000,
	business: 2000,
	enterprise: 2000,
} as const

// Included credits for a workspace. The startup/discount plan resolves to Scale
// for entitlements but gets starter-level AI credits, so callers pass that flag.
export function includedCreditsCents(plan: Plan, isStartup = false): number {
	if (isStartup) return FreeAiTokenSpendCents.starter
	return FreeAiTokenSpendCents[plan]
}
